import React, { useEffect, useMemo } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { useMapsLoader } from './MapsProvider';
import { DEFAULT_MAP_CENTER } from '../config/maps';
import { useDriverLocationsStore } from '../store/driverLocationsStore';

/**
 * A compact map showing every driver currently broadcasting a position,
 * grouped by route. Drop into a route-level page (AdminRoutes,
 * AdminRouteDensity) for an at-a-glance view of fleet activity.
 *
 * The component owns its data fetching: it pulls live driver locations on
 * mount and refreshes every 30s while mounted. No props are required.
 */

interface Props {
  height?: number;
}

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ],
};

export const RoutesOverviewMap: React.FC<Props> = ({ height = 280 }) => {
  const { isLoaded, loadError, isConfigured } = useMapsLoader();
  const live = useDriverLocationsStore((s) => s.live);
  const fetchLive = useDriverLocationsStore((s) => s.fetchLive);

  useEffect(() => {
    fetchLive(15);
    const id = setInterval(() => fetchLive(15), 30000);
    return () => clearInterval(id);
  }, [fetchLive]);

  const positions = useMemo(
    () =>
      live
        .filter((l) => Number.isFinite(l.latitude) && Number.isFinite(l.longitude))
        .map((l) => ({
          id: l.id,
          name: l.driver_name || `Driver #${l.driver_id}`,
          route: l.route_name || '—',
          position: { lat: Number(l.latitude), lng: Number(l.longitude) },
        })),
    [live]
  );

  const center = useMemo(() => {
    if (positions.length === 0) return DEFAULT_MAP_CENTER;
    const sum = positions.reduce(
      (acc, p) => ({ lat: acc.lat + p.position.lat, lng: acc.lng + p.position.lng }),
      { lat: 0, lng: 0 }
    );
    return { lat: sum.lat / positions.length, lng: sum.lng / positions.length };
  }, [positions]);

  const wrap: React.CSSProperties = {
    width: '100%',
    height,
    background: '#E8EDF2',
    borderRadius: 8,
    overflow: 'hidden',
    position: 'relative',
  };

  const overlay = (msg: string) => (
    <div style={wrap}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6B7280',
          fontSize: 13,
          textAlign: 'center',
          padding: 16,
        }}
      >
        {msg}
      </div>
    </div>
  );

  if (!isConfigured) {
    return overlay('Map unavailable. Set REACT_APP_GOOGLE_MAPS_API_KEY in .env to enable.');
  }
  if (loadError) return overlay(`Map failed to load: ${loadError.message}`);
  if (!isLoaded) return overlay('Loading map…');

  return (
    <div style={wrap}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={positions.length <= 1 ? 11 : 9}
        options={mapOptions}
      >
        {positions.map((p) => (
          <MarkerF key={p.id} position={p.position} title={`${p.name} · ${p.route}`} />
        ))}
      </GoogleMap>
      {positions.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 12,
            color: '#374151',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          }}
        >
          No drivers broadcasting right now
        </div>
      )}
    </div>
  );
};
